import { ArrowRight, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import ThemeContent from './ThemeContent';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetCaseThemes } from '@/api/queries/useGetCaseThemes';
import { BackButton } from '@/components/atoms/BackButton';
import { Button } from '@/components/atoms/new-button';
import { Fact, Summary } from '@/types';

interface ThemeFullViewProps {
  theme: Summary;
  keyFacts?: Fact[];
  caseId?: string;
  onBack: () => void;
}

const ThemeFullView = ({ theme, keyFacts = [], caseId, onBack }: ThemeFullViewProps) => {
  const navigate = useNavigate();
  const { data: themesWithCounts = [] } = useGetCaseThemes(caseId || null);

  const themeCount = themesWithCounts.find((t) => t.name === theme.name)?.count || 0;

  const handleExploreFactsClick = () => {
    if (!caseId) return;
    const searchParams = new URLSearchParams();
    searchParams.set('caseId', caseId);
    searchParams.set('themes', theme.name);
    navigate(`/app/chronos/case-editor/data/facts?${searchParams.toString()}`);
    trackCustomEvent(TrackingEvents.ViewFullThemesOnSummary);
  };

  return (
    <div className="flex flex-col leading-relaxed">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-4">
        <div className="flex items-center gap-4">
          <BackButton onClick={onBack} />
          <h1 className="text-lg font-semibold text-gray-900">{theme.name}</h1>
        </div>
        {themeCount > 0 && (
          <Button onClick={handleExploreFactsClick} variant="secondary" size="sm" className="shadow-sm">
            <Search className="h-4 w-4" />
            Explore {themeCount} Facts
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Full Content */}
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <ThemeContent
          id={`theme-full`}
          name={theme.name}
          keyFacts={keyFacts}
          subquestions={theme.subquestions}
          content={theme.content}
        />
      </div>
    </div>
  );
};

export default ThemeFullView;
